import { createContext, useContext, useMemo, useState } from "react";
import { useShallowEffect } from "@mantine/hooks";
import { FormikProps, FormikValues, useFormikContext } from "formik";
import type {
    ActionDrawerFactory,
    IActionDrawerFormikContext,
    IActionDrawerFormikProps,
    IActionDrawerFormikProviderProps,
    IProps,
} from "./action-drawer.types";
import { componentWrapper } from "../../utilities";
import { Drawer } from "../drawer";
import { ACTION_DRAWER_OFFSET, ACTION_DRAWER_SIZE } from "./action-drawer.constants";

/**
 * Drawer with primary and secondary action buttons in a footer, the drawer counterpart of ActionModal.
 * @param {string} props.title - The title of the drawer.
 * @param {React.ReactNode} props.children - The content to display in the drawer body.
 * @param {string} props.primaryButtonText - Text for the primary action button.
 * @param {string} props.secondaryButtonText - Text for the secondary/cancel button.
 * @param {object} props.primaryButtonProps - Additional props for the primary button.
 * @param {object} props.secondaryButtonProps - Additional props for the secondary button.
 * @param {function} props.onAction - Callback function when the primary action is clicked.
 * @param {function} props.onCancel - Callback function when the cancel action is clicked.
 * @returns {JSX.Element} The ActionDrawer component.
 */
const ActionDrawer = componentWrapper<ActionDrawerFactory, IProps>(
    "ActionDrawer",
    (
        {
            title,
            children,
            primaryButtonText,
            secondaryButtonText,
            primaryButtonProps,
            secondaryButtonProps,
            onAction,
            onCancel,
            ...otherProps
        },
        ref
    ) => {
        return (
            <Drawer.Root
                position="right"
                size={ACTION_DRAWER_SIZE}
                offset={ACTION_DRAWER_OFFSET}
                radius="lg"
                closeOnClickOutside
                closeOnEscape
                {...otherProps}
                ref={ref}
            >
                <Drawer.Overlay />
                <Drawer.Content>
                    <Drawer.Header>
                        <Drawer.Title>{title}</Drawer.Title>
                        <Drawer.CloseButton />
                    </Drawer.Header>
                    <Drawer.Body>{children}</Drawer.Body>
                    <Drawer.Footer>
                        <Drawer.Footer.Actions>
                            <Drawer.Footer.Actions.Cancel
                                onClick={onCancel ?? otherProps.onClose}
                                text={secondaryButtonText}
                                {...secondaryButtonProps}
                            />
                            <Drawer.Footer.Actions.Done
                                onClick={onAction}
                                text={primaryButtonText}
                                {...primaryButtonProps}
                            />
                        </Drawer.Footer.Actions>
                    </Drawer.Footer>
                </Drawer.Content>
            </Drawer.Root>
        );
    }
);

/** Carries the form's state up to the drawer, whose footer buttons sit outside the form. */
const ActionDrawerFormikContext = createContext<IActionDrawerFormikContext>({ setValues: () => {} });

/**
 * Provides the drawer's setter to the Observer inside its form.
 * @param props.children  The drawer's content, including the form.
 * @param props.setValues Called with the form's state whenever it changes.
 */
const ActionDrawerFormikProvider = <Values extends FormikValues = FormikValues>({
    children,
    setValues,
}: IActionDrawerFormikProviderProps<Values>) => {
    // The Observer only ever reports the form it sits in, so its state is of this form's values.
    const value = useMemo(() => ({ setValues: setValues as (formik: FormikProps<FormikValues>) => void }), [setValues]);
    return <ActionDrawerFormikContext.Provider value={value}>{children}</ActionDrawerFormikContext.Provider>;
};

/**
 * Placed inside the form, it reports the form's state to the drawer around it.
 */
const ActionDrawerFormikObserver = <Values extends FormikValues = FormikValues>() => {
    const { setValues } = useContext(ActionDrawerFormikContext);
    const formik = useFormikContext<Values>();
    useShallowEffect(() => {
        setValues(formik);
    }, [formik, setValues]);
    return null;
};

/**
 * ActionDrawer for a Formik form. Place `ActionDrawer.Formik.Observer` inside the form so the footer buttons, which sit
 * outside it, follow its state: Done submits the form, and is disabled until the form is changed and valid, and while
 * it is submitting. Closing the drawer is blocked while the form submits.
 */
const ActionDrawerFormik = <Values extends FormikValues = FormikValues>({
    title,
    children,
    primaryButtonText,
    secondaryButtonText,
    primaryButtonProps,
    secondaryButtonProps,
    onCancel,
    alwaysEnableSubmit = false,
    ...otherProps
}: IActionDrawerFormikProps<Values>) => {
    // Empty until the Observer reports the form's state, which it does as soon as the form mounts.
    const [formikObservedProps, setFormikObservedProps] = useState<FormikProps<Values>>({} as FormikProps<Values>);

    return (
        <Drawer.Root
            position="right"
            size={ACTION_DRAWER_SIZE}
            offset={ACTION_DRAWER_OFFSET}
            radius="lg"
            closeOnClickOutside={!formikObservedProps.isSubmitting}
            closeOnEscape={!formikObservedProps.isSubmitting}
            {...otherProps}
        >
            <Drawer.Overlay />
            <Drawer.Content>
                <ActionDrawerFormikProvider<Values> setValues={setFormikObservedProps}>
                    <Drawer.Header>
                        <Drawer.Title>{typeof title === "function" ? title(formikObservedProps) : title}</Drawer.Title>
                        <Drawer.CloseButton disabled={formikObservedProps.isSubmitting} />
                    </Drawer.Header>
                    <Drawer.Body>{children}</Drawer.Body>
                    <Drawer.Footer>
                        <Drawer.Footer.Actions>
                            <Drawer.Footer.Actions.Cancel
                                onClick={onCancel ?? otherProps.onClose}
                                text={secondaryButtonText}
                                disabled={formikObservedProps?.isSubmitting}
                                {...(typeof secondaryButtonProps === "function"
                                    ? secondaryButtonProps(formikObservedProps)
                                    : secondaryButtonProps)}
                            />
                            <Drawer.Footer.Actions.Done
                                type="submit"
                                text={primaryButtonText}
                                onClick={formikObservedProps.submitForm}
                                loading={formikObservedProps.isSubmitting}
                                disabled={
                                    (!alwaysEnableSubmit && !formikObservedProps?.dirty) ||
                                    !formikObservedProps?.isValid ||
                                    formikObservedProps?.isSubmitting
                                }
                                {...(typeof primaryButtonProps === "function"
                                    ? primaryButtonProps(formikObservedProps)
                                    : primaryButtonProps)}
                            />
                        </Drawer.Footer.Actions>
                    </Drawer.Footer>
                </ActionDrawerFormikProvider>
            </Drawer.Content>
        </Drawer.Root>
    );
};

ActionDrawerFormik.Observer = ActionDrawerFormikObserver;

ActionDrawer.Formik = ActionDrawerFormik;

export { ActionDrawer, ActionDrawerFormik };
